<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import MediaBuyingInfo from '@/views/MediaBuying/components/MediaBuyingInfo.vue';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import type { GetMediaBuyingPostsByIdResponse, GetMediaBuyingPostsByIdData, PutCommentsByIdData } from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingPostsQuery = NonNullable<GetMediaBuyingPostsByIdData['query']>;
type MediaBuyingPostsPermission = NonNullable<GetMediaBuyingPostsByIdResponse['misc']>['permissions'];
//#endregion

//#region const
const LS_PATH = 'MediaBuying/Post';

const mapStatus = {
  approve: 'declined',
  decline: 'approved',
  'for-rework': 'for_rework',
  'to-review': 'on_review',
} as const;
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const router = useRouter();
const store = useTypedStore();
const ls = useLs();
const popupManager = usePopupManager();
const { t } = useI18n();
const { checkRequest, checkRoute } = useDetectPermissions();
//#endregion

//#region state
const isDataLoading = ref(true);
const info = ref({} as GetMediaBuyingPostsByIdResponse['payload']);
const permissions = ref({} as MediaBuyingPostsPermission);
const currencyCode = ref('');
const periodStats = ref<any>(null);
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const currentAcl = computed(() => store.getters['auth/currentAcl']);

const postId = computed(() => {
  return route.params.id as string;
});

const hideToolbar = computed(() => {
  return (
    (adminAcl.value.is_superuser
      && (info.value.status === 'on_review'
        || info.value.status === 'for_rework'))
    || (!adminAcl.value.is_superuser
      && permissions.value.review_actions
      && (info.value.status === 'on_review'
        || info.value.status === 'for_rework'))
  );
});

const backPage = computed(() => {
  return {
    title: `${t('mediaBuying.page_title.posts.name')} — ${t(`mediaBuying.page_title.posts.${info.value.status}`)}`,
    link: `/media-buying/posts/${info.value.status?.replace('_', '-')}`,
  };
});

const breadcrumbs = computed(() => {
  return [
    backPage.value,
    {
      title: info.value.affiliate_email,
    },
  ];
});
//#endregion

//#region methods
const _query = () => {
  if(periodStats.value) {
    const { stats_period_start, stats_period_end } = periodStats.value;

    return {
      stats_period_start: stats_period_start ? format(startOfDay(stats_period_start), 'yyyy-MM-dd HH:mm:ss') : '',
      stats_period_end: stats_period_end ? format(endOfDay(stats_period_end), 'yyyy-MM-dd HH:mm:ss') : '',
    } as MediaBuyingPostsQuery;
  }

  return {} as MediaBuyingPostsQuery;
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._query(),
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData() {
  if (!postId.value) return;

  isDataLoading.value = true;

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingPostsInfo(postId.value, options);
    permissions.value = misc!.permissions;
    currencyCode.value = misc!.currency_code;
    info.value = payload;

    checkRoute(
      'media_buying_posts_view',
      'media_buying_posts_statuses_visible',
      info.value.status,
    );
  } finally {
    isDataLoading.value = false;
  }
}

async function initData() {
  if (checkRequest('media_buying_posts_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    const period = !_.isEmpty(_urlData) ? _urlData : ls.get(`${LS_PATH}/period`) || null;
    if(period) {
      const { stats_period_start, stats_period_end } = period;
      if (stats_period_start && stats_period_end) {
        periodStats.value = {
          stats_period_start,
          stats_period_end,
        };
      }
    }

    nextTick(async () => {
      await initNeedsAttention();
      fetchData();
    });
  }
  checkRoute('media_buying_posts_view');
}

async function initNeedsAttention() {
  if (route.query.needs === 'success') {
    await api.postMediaBuyingPostsMarkAsRead(postId.value as string);
    router.replace({
      path: route.path,
      query: { ...route.query, needs: undefined },
    });
  }
}

async function markAsRead() {
  if (info.value.needs_attention) {
    await api.postMediaBuyingPostsMarkAsRead(info.value.id);
  } else {
    await api.postMediaBuyingPostsMarkAsUnread(info.value.id);
  }
  fetchData();
}

function applyCampaignStatistics(period: string[]) {
  if (period) {
    const [stats_period_start, stats_period_end] = period;
    periodStats.value = {
      stats_period_start,
      stats_period_end,
    };
    ls.set(`${LS_PATH}/period`, {
      stats_period_start,
      stats_period_end,
    });
  } else {
    periodStats.value = null;
    ls.set(`${LS_PATH}/period`, null);
  }

  fetchData();
}

async function changeStatus(status: keyof typeof mapStatus) {
  await api.postMediaBuyingPostsChangeStatus(postId.value, status);

  if (adminAcl.value.is_superuser) {
    await fetchData();
  } else if (currentAcl.value.media_buying_posts_statuses_visible.includes(mapStatus[status])) {
    await fetchData();
  } else {
    await router.push(`/media-buying/posts/${info.value.status ? info.value.status.replace('_', '-') : ''}`);
  }
}

function approveHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.approve_post.text'),
      title: t('mediaBuying.info.popup.approve_post.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.approve_post.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        changeStatus('approve');
        context.handleClose();
      },
    },
  });
}

function reworkHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.rework_post.text'),
      title: t('mediaBuying.info.popup.rework_post.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.rework_post.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        changeStatus('for-rework');
        context.handleClose();
      },
    },
  });
}

function declineHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.decline_post.text'),
      title: t('mediaBuying.info.popup.decline_post.title'),
      bodyIcon: 'ban',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.decline_post.button'),
      confirmType: 'negative',
      confirmIcon: 'ban',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        changeStatus('decline');
        context.handleClose();
      },
    },
  });
}

function reviewHandler() {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.on_review_post.text'),
      title: t('mediaBuying.info.popup.on_review_post.title'),
      bodyIcon: 'exclamation-triangle',
      confirmTitle: t('mediaBuying.info.popup.on_review_post.button'),
      confirmType: 'positive',
      confirmIcon: 'check',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        changeStatus('to-review');
        context.handleClose();
      },
    },
  });
}

function addToBlacklist() {
  popupManager.postBlacklistPopup.open({
    data: {
      id: info.value.campaign_id,
      urls: info.value.urls ?? [],
    },
    listeners: {
      success: fetchData,
    },
  });
}

function postComment(comment: PutCommentsByIdData['body']) {
  return api.postMediaBuyingPostsComments(postId.value, comment);
}

function getComments() {
  return api.getMediaBuyingPostsComments(postId.value);
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <MediaBuyingInfo
    class="media-buying-post-info"
    :breadcrumbs="breadcrumbs"
    :is-loading="isDataLoading"
    :info="info"
    :currency-code="currencyCode"
    :period="periodStats"
    :permissions="permissions"
    :get-comments="getComments"
    :post-comment="postComment"
    type="post"
    @apply-filters="applyCampaignStatistics"
  >
    <template v-slot:buttons>
      <template v-if="hideToolbar">
        <UiButton
          v-if="info.status !== 'for_rework'"
          icon="check"
          filled
          @click="approveHandler()"
        >
          {{ $t('mediaBuying.actions.approve') }}
        </UiButton>
        <UiButton
          v-if="info.status !== 'for_rework'"
          icon="sync-alt"
          @click="reworkHandler()"
        >
          {{ $t('mediaBuying.actions.send_for_rework') }}
        </UiButton>
      </template>
      <UiButton
        :icon="info.needs_attention ? 'circle-exclamation-check' : 'exclamation-triangle'"
        :type="info.needs_attention ? 'positive' : 'negative'"
        @click="markAsRead"
      >
        {{ !info.needs_attention ? $t('mediaBuying.other.btn_mark') : $t('mediaBuying.other.btn_unmark') }}
      </UiButton>
      <template v-if="hideToolbar">
        <UiButton
          icon="ban"
          type="negative"
          @click="addToBlacklist()"
        >
          {{ $t('mediaBuying.other.add_to_blacklist') }}
        </UiButton>
        <UiButton
          v-if="info.status !== 'for_rework'"
          icon="times"
          type="negative"
          @click="declineHandler()"
        >
          {{ $t('mediaBuying.actions.decline') }}
        </UiButton>
        <UiButton
          v-if="info.status === 'for_rework'"
          icon="check-double"
          @click="reviewHandler()"
        >
          {{ $t('mediaBuying.actions.send_to_review') }}
        </UiButton>
      </template>
    </template>
  </MediaBuyingInfo>
</template>
